import React, { useEffect, useState, useRef } from "react";

const About = () => {
  const sections = [
    {
      id: "mission",
      title: "Our Mission",
      content:
        "At Codex Future Technologies Pvt. Ltd., we empower businesses to succeed in the digital world through innovative, scalable, and user-focused IT solutions.",
      image: "/our-mission.jpg",
    },
    {
      id: "vision",
      title: "Our Vision",
      content:
        "We aim to be the most trusted technology partner, delivering high-quality web development, mobile applications, digital marketing, and cloud solutions to help businesses grow.",
      image: "/our-vission.jpg",
    },
    {
      id: "values",
      title: "Our Values",
      content:
        "💡 We’re not just developers—we’re your technology partner for the future. Creativity, technical expertise, and integrity drive everything we do.",
      image: "/partner.jpg",
    },
    {
      id: "experience",
      title: "What We Offer",
      content:
        "From startups to enterprises, we help organizations embrace digital transformation through web development, mobile app development, digital marketing, and cloud-based solutions.",
      image: "/offer.jpg",
    },
    {
      id: "future",
      title: "Our Promise",
      content:
        "At Codex Future Technologies, we believe in building long-term partnerships by providing continuous support, innovation, and value-driven services. 🚀",
      image: "/promise.jpg",
    },
  ];

  const sectionRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.indexOf(entry.target);
            if (idx !== -1) setActiveIndex(idx);
          }
        });
      },
      { threshold: 0.6 } // more than half must be visible
    );

    sectionRefs.current.forEach((section) => section && observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleScrollDown = () => {
    sectionRefs.current[0]?.scrollIntoView({ behavior: "smooth" });
  };

 return (
  <div className="relative w-full font-sans overflow-x-hidden bg-black text-white">
    {/* HERO SECTION */}
    <section className="relative w-full h-[40vh] sm:h-[90vh] flex flex-col justify-center items-center text-center px-6 bg-black">
      <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-4 uppercase tracking-wide mt-16 text-[#D4AF37] drop-shadow-[0_0_15px_rgba(212,175,55,0.5)]">
        About Us
      </h1>
      <p className="mt-2 text-lg sm:text-xl max-w-2xl leading-relaxed text-gray-300">
        At <strong className="text-[#f5d68a]">Codex Future Technologies</strong>, we deliver innovative web, mobile, 
        and cloud solutions that drive business growth, scalability, and digital transformation.
      </p>

      <button
        onClick={handleScrollDown}
        className="absolute -bottom-1 animate-bounce text-black bg-[#D4AF37] w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300"
      >
        ↓
      </button>
    </section>

    {/* ABOUT SECTIONS */}
    <div className="relative py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
        {sections.map((section, index) => {
          const isLeft = index % 2 === 0;
          const isActive = index === activeIndex;

          return (
            <div
              key={section.id}
              ref={(el) => (sectionRefs.current[index] = el)}
              className={`flex flex-col ${
                isLeft ? "md:flex-row" : "md:flex-row-reverse"
              } items-center gap-10 md:gap-16 lg:gap-24 transition-all duration-700`}
              style={{
                filter: isActive ? "none" : "grayscale(90%) brightness(60%)",
                transform: isActive ? "scale(1)" : "scale(0.97)",
              }}
            >
              {/* IMAGE */}
              <div className="w-full md:w-1/2 rounded-3xl overflow-hidden shadow-[0_0_30px_rgba(212,175,55,0.3)] transform transition-transform duration-500 hover:scale-105">
                <img
                  src={section.image}
                  alt={section.title}
                  className="w-full h-56 sm:h-72 md:h-96 lg:h-[28rem] object-cover"
                />
              </div>

              {/* TEXT */}
              <div className="w-full md:w-1/2 px-2 sm:px-4 text-center md:text-left transition-colors duration-500">
                <h2
                  className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-snug ${
                    isActive ? "text-[#D4AF37]" : "text-gray-500"
                  }`}
                >
                  {section.title}
                </h2>
                <p
                  className={`text-base sm:text-lg leading-relaxed tracking-wide ${
                    isActive ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {section.content}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Gold Ambient Background Glows */}
    <div className="absolute inset-0 -z-10 pointer-events-none">
      <div className="absolute w-[900px] h-[900px] bg-[#D4AF37]/20 rounded-full blur-[220px] top-1/2 left-[-200px] animate-float-smooth"></div>
      <div className="absolute w-[700px] h-[700px] bg-[#f5d68a]/20 rounded-full blur-[220px] bottom-0 right-[-200px] animate-float-smooth"></div>
    </div>

    <style>{`
      @keyframes floatSmooth {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      .animate-float-smooth {
        animation: floatSmooth 8s ease-in-out infinite;
      }
    `}</style>
  </div>
);

};

export default About;
